import { createFileRoute, Link } from "@tanstack/react-router";
import { format, startOfWeek } from "date-fns";
import { useState } from "react";
import { ArrowUpRight, CalendarDays, Trash2 } from "lucide-react";

import {
  useDeleteMeeting,
  useMeetingsList,
} from "@/application/use-cases/meetings/use-meetings.queries";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@monorepo-apps/ui/src/components/alert-dialog";

export const Route = createFileRoute("/_app/meetings/")({
  component: MeetingsListPage,
});

function MeetingsListPage() {
  const meetingsQ = useMeetingsList();
  const deleteMeeting = useDeleteMeeting();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);
  const meetings = meetingsQ.data ?? [];

  const groups = new Map<string, typeof meetings>();
  for (const m of meetings) {
    const key = format(startOfWeek(new Date(m.meeting_at), { weekStartsOn: 1 }), "d MMM yyyy");
    const arr = groups.get(key) ?? [];
    arr.push(m);
    groups.set(key, arr);
  }

  const now = new Date();
  const upcomingCount = meetings.filter((m) => new Date(m.meeting_at) >= now).length;
  const pastCount = meetings.length - upcomingCount;
  const lastMeeting = meetings[0];

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="meetings" />
      </div>
      <RouteHero
        eyebrow="Meeting Log"
        icon={CalendarDays}
        numeral={String(meetings.length).padStart(2, "0")}
        numeralTone="green"
        numeralSuffix={upcomingCount > 0 ? `${upcomingCount} upcoming` : "logged"}
        title="Conversations on file"
        summary="Every interview, gate review, and working session that's shaped the assessment."
        meta={[
          { label: "Upcoming", value: upcomingCount, tone: "amber" },
          { label: "Past", value: pastCount, tone: "green" },
          {
            label: "Last",
            value: lastMeeting ? format(new Date(lastMeeting.meeting_at), "d MMM") : "—",
          },
          { label: "Weeks", value: groups.size },
        ]}
        actions={
          <Link
            to="/meetings/new"
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="font-data text-[11px] tracking-[0.12em] uppercase">+ New meeting</span>
            <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        }
      />

      <div>
        {meetings.length === 0 ? (
          <p className="text-muted-foreground py-8 text-xs italic">
            No meetings logged yet — kickoff is the first one (target: first week of May).
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            {[...groups.entries()].map(([weekKey, items]) => (
              <section
                key={weekKey}
                className="glow-card flex flex-col gap-3 p-5 print:break-inside-avoid"
              >
                <h2 className="eyebrow">Week of {weekKey}</h2>
                <ul className="flex flex-col">
                  {items.map((m) => (
                    <li
                      key={m.id}
                      className="border-border/40 group flex items-center gap-3 border-t py-2 first:border-t-0 print:break-inside-avoid"
                    >
                      <span className="text-muted-foreground font-data w-28 shrink-0 text-[10px] tracking-wider uppercase tabular-nums">
                        {format(new Date(m.meeting_at), "EEE d MMM, HH:mm")}
                      </span>
                      <Link
                        to="/meetings/view/$slug"
                        params={{ slug: m.slug }}
                        className="flex-1 text-xs hover:underline"
                      >
                        {m.title}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setPendingDelete({ id: m.id, title: m.title })}
                        className="text-muted-foreground hover:text-destructive opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 print:hidden"
                        aria-label={`Delete ${m.title}`}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `"${pendingDelete.title}" will be permanently removed, along with its agenda, notes, attendees, and links.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingDelete) return;
                deleteMeeting.mutate(pendingDelete.id, {
                  onSuccess: () => setPendingDelete(null),
                });
              }}
              disabled={deleteMeeting.isPending}
            >
              {deleteMeeting.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
